// TypeScript interfaces for the Used Cars feature
// Based on the server-side schema definitions

// Helper function to get human-readable labels for enum values can be imported when needed
// import { getEnumLabel } from '../utils/formatters';

// Enum types
export enum Manufacturer {
  CHEVROLET = 'chevrolet',
  FORD = 'ford',
  TOYOTA = 'toyota',
  JEEP = 'jeep',
  HONDA = 'honda',
  DODGE = 'dodge',
  CHRYSLER = 'chrysler',
  ALFA_ROMEO = 'alfa-romeo',
  SUBARU = 'subaru',
  ACURA = 'acura',
  BMW = 'bmw',
  VOLKSWAGEN = 'volkswagen',
  MAZDA = 'mazda',
  LEXUS = 'lexus',
  NISSAN = 'nissan',
  KIA = 'kia',
  BUICK = 'buick',
  HYUNDAI = 'hyundai',
  GMC = 'gmc',
  INFINITI = 'infiniti',
  PORSCHE = 'porsche',
  CADILLAC = 'cadillac',
  RAM = 'ram',
  MERCEDES_BENZ = 'mercedes-benz',
  VOLVO = 'volvo',
  PONTIAC = 'pontiac',
  AUDI = 'audi',
  MITSUBISHI = 'mitsubishi',
  LINCOLN = 'lincoln',
  FIAT = 'fiat',
  ROVER = 'rover',
  MINI = 'mini',
  JAGUAR = 'jaguar',
  SATURN = 'saturn',
  MERCURY = 'mercury',
  HARLEY_DAVIDSON = 'harley-davidson',
  LAND_ROVER = 'land rover',
  TESLA = 'tesla',
  ASTON_MARTIN = 'aston-martin',
  FERRARI = 'ferrari',
}

export enum Fuel {
  GAS = 'gas',
  OTHER = 'other',
  DIESEL = 'diesel',
  HYBRID = 'hybrid',
  ELECTRIC = 'electric',
}

export enum Transmission {
  OTHER = 'other',
  AUTOMATIC = 'automatic',
  MANUAL = 'manual',
}

export enum Drive {
  FOUR_WHEEL_DRIVE = '4wd',
  REAR_WHEEL_DRIVE = 'rwd',
  FRONT_WHEEL_DRIVE = 'fwd',
}

export enum Size {
  FULL_SIZE = 'full-size',
  COMPACT = 'compact',
  MID_SIZE = 'mid-size',
  SUB_COMPACT = 'sub-compact',
}

export enum VehicleType {
  PICKUP = 'pickup',
  TRUCK = 'truck',
  OTHER = 'other',
  MINI_VAN = 'mini-van',
  SUV = 'SUV',
  SEDAN = 'sedan',
  HATCHBACK = 'hatchback',
  OFFROAD = 'offroad',
  CONVERTIBLE = 'convertible',
  COUPE = 'coupe',
  WAGON = 'wagon',
  VAN = 'van',
  BUS = 'bus',
}

export enum PaintColor {
  BLUE = 'blue',
  BLACK = 'black',
  GREY = 'grey',
  RED = 'red',
  SILVER = 'silver',
  WHITE = 'white',
  GREEN = 'green',
  YELLOW = 'yellow',
  CUSTOM = 'custom',
  BROWN = 'brown',
  PURPLE = 'purple',
  ORANGE = 'orange',
}

export enum State {
  ALABAMA = 'al',
  ALASKA = 'ak',
  ARIZONA = 'az',
  ARKANSAS = 'ar',
  CALIFORNIA = 'ca',
  COLORADO = 'co',
  CONNECTICUT = 'ct',
  DISTRICT_OF_COLUMBIA = 'dc',
  DELAWARE = 'de',
  FLORIDA = 'fl',
  GEORGIA = 'ga',
  HAWAII = 'hi',
  IDAHO = 'id',
  ILLINOIS = 'il',
  INDIANA = 'in',
  IOWA = 'ia',
  KANSAS = 'ks',
  KENTUCKY = 'ky',
  LOUISIANA = 'la',
  MAINE = 'me',
  MARYLAND = 'md',
  MASSACHUSETTS = 'ma',
  MICHIGAN = 'mi',
  MINNESOTA = 'mn',
  MISSISSIPPI = 'ms',
  MISSOURI = 'mo',
  MONTANA = 'mt',
  NORTH_CAROLINA = 'nc',
  NEBRASKA = 'ne',
  NEVADA = 'nv',
  NEW_JERSEY = 'nj',
  NEW_MEXICO = 'nm',
  NEW_YORK = 'ny',
  NEW_HAMPSHIRE = 'nh',
  NORTH_DAKOTA = 'nd',
  OHIO = 'oh',
  OKLAHOMA = 'ok',
  OREGON = 'or',
  PENNSYLVANIA = 'pa',
  RHODE_ISLAND = 'ri',
  SOUTH_CAROLINA = 'sc',
  SOUTH_DAKOTA = 'sd',
  TENNESSEE = 'tn',
  TEXAS = 'tx',
  UTAH = 'ut',
  VERMONT = 'vt',
  VIRGINIA = 'va',
  WASHINGTON = 'wa',
  WEST_VIRGINIA = 'wv',
  WISCONSIN = 'wi',
  WYOMING = 'wy',
}

export enum TitleStatus {
  CLEAN = 'clean',
  REBUILT = 'rebuilt',
  LIEN = 'lien',
  SALVAGE = 'salvage',
  MISSING = 'missing',
  PARTS_ONLY = 'parts only',
}

export enum Condition {
  GOOD = 'good',
  EXCELLENT = 'excellent',
  FAIR = 'fair',
  LIKE_NEW = 'like new',
  NEW = 'new',
  SALVAGE = 'salvage',
}

// Input interface
export interface CarInput {
  manufacturer: Manufacturer;
  fuel: Fuel;
  transmission: Transmission;
  drive: Drive;
  size: Size;
  vehicle_type: VehicleType;
  paint_color: PaintColor;
  state: State;
  title_status: TitleStatus;
  condition: Condition;
  year: number;
  odometer: number;
  cylinders: number;
}

// Response interface
export interface CarResponse extends CarInput {
  price: number;
}

// Helper function to get current year
export const getCurrentYear = (): number => {
  return new Date().getFullYear();
};

// Helper function to format price
export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};
